<template>
  <Teleport to="body">
    <div class="notification-container" :class="{ 'has-header': hasHeader }">
      <TransitionGroup name="notification" tag="div">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          :class="getNotificationClasses(notification)"
          @click="handleClick(notification)"
        >
          <div class="notification-icon">
            <component :is="getIcon(notification.type)" />
          </div>
          
          <div class="notification-content">
            <h4 v-if="notification.title" class="notification-title">
              {{ notification.title }}
            </h4>
            <p class="notification-message">
              {{ notification.message }}
            </p>
          </div>
          
          <button
            v-if="notification.closable !== false"
            type="button"
            class="notification-close"
            @click.stop="removeNotification(notification.id)"
          >
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'

// 全局通知状态
const notifications = ref([])
let notificationId = 0

// 图标组件
const CheckIcon = {
  template: `
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
    </svg>
  `
}

const InfoIcon = {
  template: `
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
    </svg>
  `
}

const WarningIcon = {
  template: `
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path fill="currentColor" d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
    </svg>
  `
}

const ErrorIcon = {
  template: `
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path fill="currentColor" d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
    </svg>
  `
}

export default {
  name: 'BaseNotification',
  setup() {
    const hasHeader = ref(false)
    
    const checkHeader = () => {
      hasHeader.value = !!document.querySelector('header')
    }
    
    onMounted(() => {
      checkHeader()
      // 监听DOM变化，以防header动态添加/移除
      const observer = new MutationObserver(checkHeader)
      observer.observe(document.body, { childList: true, subtree: true })
    })
    
    const removeNotification = (id) => {
      const index = notifications.value.findIndex(n => n.id === id)
      if (index > -1) {
        notifications.value.splice(index, 1)
      }
    }

    const addNotification = (options) => {
      const notification = {
        id: ++notificationId,
        type: 'info',
        title: '',
        message: '',
        duration: 4000,
        closable: true,
        onClick: null,
        ...options
      }

      notifications.value.push(notification)

      // 自动移除
      if (notification.duration > 0) {
        setTimeout(() => {
          removeNotification(notification.id)
        }, notification.duration)
      }

      return notification.id
    }

    const getIcon = (type) => {
      const icons = {
        success: CheckIcon,
        info: InfoIcon,
        warning: WarningIcon,
        error: ErrorIcon
      }
      return icons[type] || InfoIcon
    }

    const getNotificationClasses = (notification) => {
      return [
        'notification',
        `notification--${notification.type}`,
        {
          'notification--clickable': notification.onClick
        }
      ]
    }

    const handleClick = (notification) => {
      if (notification.onClick) {
        notification.onClick(notification)
      }
    }

    // 全局方法
    window.$notify = {
      success: (message, options = {}) => addNotification({ ...options, type: 'success', message }),
      info: (message, options = {}) => addNotification({ ...options, type: 'info', message }),
      warning: (message, options = {}) => addNotification({ ...options, type: 'warning', message }),
      error: (message, options = {}) => addNotification({ ...options, type: 'error', message }),
      remove: removeNotification,
      clear: () => { notifications.value = [] }
    }

    return {
      notifications,
      hasHeader,
      removeNotification,
      getIcon,
      getNotificationClasses,
      handleClick
    }
  }
}
</script>

<style scoped>
.notification-container {
  position: fixed;
  top: var(--spacing-lg);
  right: var(--spacing-lg);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  max-width: 400px;
  width: 100%;
}

/* 当页面有header时，调整通知位置避免遮盖 */
.notification-container.has-header {
  top: 80px; /* header高度 + 间距 */
}

.notification {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background-color: var(--color-surface);
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-2);
  border-left: 4px solid;
  margin-bottom: var(--spacing-sm);
  min-height: 64px;
  position: relative;
  overflow: hidden;
}

.notification--clickable {
  cursor: pointer;
}

.notification--clickable:hover {
  box-shadow: var(--shadow-3);
}

/* 类型样式 */
.notification--success {
  border-left-color: var(--color-secondary);
}

.notification--success .notification-icon {
  color: var(--color-secondary);
}

.notification--info {
  border-left-color: var(--color-primary);
}

.notification--info .notification-icon {
  color: var(--color-primary);
}

.notification--warning {
  border-left-color: var(--color-warning);
}

.notification--warning .notification-icon {
  color: var(--color-warning);
}

.notification--error {
  border-left-color: var(--color-error);
}

.notification--error .notification-icon {
  color: var(--color-error);
}

/* 内容区域 */
.notification-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-on-surface);
  margin: 0 0 var(--spacing-xs) 0;
  line-height: 1.4;
}

.notification-message {
  font-size: 14px;
  color: var(--color-on-surface-variant);
  margin: 0;
  line-height: 1.4;
  word-break: break-word;
}

.notification-close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  color: var(--color-on-surface-secondary);
  cursor: pointer;
  border-radius: var(--border-radius-small);
  transition: all 0.2s ease;
  margin-top: -2px;
}

.notification-close:hover {
  background-color: var(--color-surface-variant);
  color: var(--color-on-surface);
}

/* 动画 */
.notification-enter-active {
  transition: all 0.3s ease;
}

.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s ease;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .notification-container {
    top: var(--spacing-lg);
    right: var(--spacing-sm);
    left: auto;
    max-width: 65vw;
  }
  
  .notification-container.has-header {
    top: 70px; /* 移动端header高度 + 间距 */
  }
  
  .notification {
    padding: var(--spacing-sm);
  }
  
  .notification-title,
  .notification-message {
    font-size: 13px;
  }
}
</style>
